import WidgetForm from "./WidgetForm";
import React from "react";
import {Formik} from "formik";
import Button from "../../../../../base/components/Button";
import {BUTTON_COLORS} from "../../../../../base/components/Button/appearance";
import {ModalFooter} from "reactstrap";
import {useService} from "../../../../../base/hooks/useService";
import DynamicWidgetsService from "../../../../../services/DynamicWidgetsService";
import {useLoading} from "../../../../../base/hooks/useLoading";
import {validationSchema} from "./form";
import {CustomModal, ModalBody, ModalHeader} from "../../../../../base/components/CustomModal";
import ToasterService from "../../../../../services/ToastService";
import {DYNAMIC_WIDGET_FILE_TYPE} from "../../../../../base/constants/shared";
import useUploadImages from "../../../../../base/components/Dropzone/useUploadImages";

const EditWidgetModal = ({onClose, isOpen, widget, afterEdit}) => {
    /**
     * @type {DynamicWidgetsService}
     */
    const widgetsService = useService(DynamicWidgetsService);
    /**
     * @type {ToasterService}
     */
    const toastService = useService(ToasterService);

    const [isLoading, {registerPromise}] = useLoading(false);
    const uploadImage = useUploadImages();

    const initialValues = {
        ...widget,
        icon: [{
            ...widget.icon,
            preview: widget.icon.link,
            file: { size: widget.icon.bytes ?? 0 },
        }],
        placeholderIcon: [{
            ...widget.placeholderIcon,
            preview: widget.placeholderIcon.link,
            file: { size: widget.placeholderIcon.bytes ?? 0 },
        }]
    }

    const onSuccess = () => {
        toastService.success('Widget has been successfully updated')
        onClose()
    }

    const onFinally = (formikHelpers) => {
        afterEdit()
        formikHelpers.setSubmitting(false);
    }

    const editWidget = async (values, formikHelpers) => {
        formikHelpers.setSubmitting(true);
        const {icon, placeholderIcon, label, unit, status} = values
        if (icon[0]?.id && placeholderIcon[0]?.id) {
            const newData = {
                label,
                unit,
                status,
                iconId: icon[0].id,
                placeholderIconId: placeholderIcon[0].id,
            }
            return registerPromise(widgetsService.editAdminsWidget(widget.id, newData))
                .then(onSuccess)
                .finally(() => onFinally(formikHelpers))
        }

        let iconNew = icon[0];
        let placeholderIconNew = placeholderIcon[0];

        if (!icon[0]?.id) {
            const { file: uploadedIconFile } = await uploadImage(icon[0].file, DYNAMIC_WIDGET_FILE_TYPE)
            iconNew = uploadedIconFile
        }

        if (!placeholderIcon[0]?.id) {
            const { file: uploadedPlaceholderIconFile } = await uploadImage(placeholderIcon[0].file, DYNAMIC_WIDGET_FILE_TYPE)
            placeholderIconNew = uploadedPlaceholderIconFile
        }

        const newData = {
            label,
            unit,
            status,
            iconId: iconNew.id,
            placeholderIconId: placeholderIconNew.id,
        }

        return registerPromise(widgetsService.editAdminsWidget(widget.id, newData))
            .then(onSuccess)
            .finally(() => onFinally(formikHelpers))
    }

    return(
        <CustomModal scrollable isOpen={isOpen} className="widget-modal fontStyles" size="md">
            <ModalHeader onClose={() => onClose()} className="font-weight-semibold text-dark">
                Edit widget
            </ModalHeader>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur
                onSubmit={(values, formikHelpers) => {
                    return editWidget(values, formikHelpers)
                }}
                enableReinitialize
            >
                {({values, handleSubmit, isSubmitting, isValid, touched}) => {
                    return (
                        <>
                            <ModalBody className="p-3">
                                <WidgetForm values={values}/>
                            </ModalBody>
                            <ModalFooter>
                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        color={BUTTON_COLORS.primaryOutline}
                                        type="button"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        color={BUTTON_COLORS.primary}
                                        type="submit"
                                        disabled={isSubmitting || !(isValid && touched) || !values.icon?.length || !values.placeholderIcon?.length}
                                        loading={isLoading}
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </ModalFooter>
                        </>
                    )
                }}
            </Formik>
        </CustomModal>
    )
}

export default EditWidgetModal;